import { Add, Delete, Edit, Home, Refresh } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  Button,
  CardActions,
  CardContent,
  Checkbox,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { COLORS } from "assets";
import { BulkActionBar, DataTable, KebabMenu, PageToolbar, StyledCard, bulkCheckboxSx, bulkDeleteButtonSx, bulkPrimaryButtonSx } from "components";
import StyledTablePagination from "components/StyledTablePagination";
import { BREAK_GLASS_APPROVED_EVENT, ENDPOINTS } from "config/constants";
import { useContextController } from "context";
import { useFetch, useTitle } from "hooks";
import useTranslation from "hooks/useTranslation";
import PrivateLayout from "layouts/PrivateLayout";
import { ChangeEventHandler, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteModel } from "types";
import {
  canDeleteSiteRecord,
  canEditSiteRecord,
  canManageAnySite,
  dateFormat,
  ensureBreakGlassForSite,
  getCountryName
} from "utils";
import BulkRemoveSites from "./bulk-remove";
import BulkManageSites from "./manage-bulk";
import RemoveSite from "./remove";

interface Data {
  sites?: SiteModel[];
  totalRecords?: number;
}

interface ISiteRowActions {
  site: SiteModel;
  onRemove: (site: SiteModel) => void;
}

function SiteRowActions({ site, onRemove }: ISiteRowActions) {
  const t = useTranslation();
  const navigate = useNavigate();
  const [{ userData }] = useContextController();

  const canEdit = canEditSiteRecord(userData, site._id);
  const canDelete = canDeleteSiteRecord(userData);
  const items = [];

  if (canEdit) {
    items.push({
      key: "edit",
      label: t["Edit"],
      icon: <Edit fontSize="small" />,
      onClick: () => {
        const path = `/sites/${site._id}`;
        if (!ensureBreakGlassForSite(userData?.type, site._id)) {
          const onApproved = (event: Event) => {
            const approved = (event as CustomEvent<{ siteId?: string }>).detail?.siteId;
            if (String(approved) === String(site._id)) {
              navigate(path);
              window.removeEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
            }
          };
          window.addEventListener(BREAK_GLASS_APPROVED_EVENT, onApproved);
          return;
        }
        navigate(path);
      }
    });
  }
  if (canDelete) {
    items.push({
      key: "delete",
      label: t["Delete"],
      icon: <Delete fontSize="small" />,
      destructive: true,
      onClick: () => onRemove(site)
    });
  }

  if (!items.length) return null;

  return (
    <Stack direction="row" justifyContent="center" alignItems="center">
      <KebabMenu items={items} />
    </Stack>
  );
}

export default function Sites() {
  const [{ userData }] = useContextController();
  const [removing, setRemoving] = useState<SiteModel>();
  const [bulkManage, setBulkManage] = useState(false);
  const [bulkRemove, setBulkRemove] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reload, setReload] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<keyof SiteModel>();
  const [isAscending, setAscending] = useState(false);

  const t = useTranslation();
  const { isLoading, response, error, callFetch: callFetchGet } = useFetch<Data>("GET");
  const { sites, totalRecords } = useMemo(() => {
    const data = response && response.data;
    if (data) {
      return data;
    }
    return {};
  }, [response]);
  const searchWaitRef = useRef<NodeJS.Timeout>();
  const onSort = useCallback(
    (val: keyof SiteModel) => {
      if (val === sort) {
        if (isAscending) {
          setAscending(false);
        } else {
          setSort(undefined);
        }
      } else {
        setSort(val);
        setAscending(true);
      }
    },
    [sort, isAscending]
  );
  const onReload = useCallback(() => setReload((prevState) => !prevState), []);
  const onPageChange = (_: any, page: number) => setPage(page);
  const onRowsPerPageChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = ({ target: { value } }) => {
    setLimit(Number.parseInt(value, 10));
    setPage(0);
  };

  useEffect(() => {
    let params = `?limit=${limit}&page=${page + 1}`;
    if (search) {
      params += `&search=${search}`;
    }
    if (sort) {
      params += `&sort=${isAscending ? "" : "-"}${sort}`;
    }
    if (searchWaitRef.current) {
      clearTimeout(searchWaitRef.current);
    }
    searchWaitRef.current = setTimeout(() => {
      callFetchGet(ENDPOINTS.sites + params).catch(console.error);
    }, 400);
    return () => {
      if (searchWaitRef.current) clearTimeout(searchWaitRef.current);
    };
  }, [page, limit, search, sort, isAscending, reload, callFetchGet]);

  useEffect(() => setPage(0), [search]);
  useEffect(() => setSelectedIds([]), [page, limit, search, sort, isAscending]);

  useTitle("Sites");

  const showActions = canManageAnySite(userData) || canDeleteSiteRecord(userData);
  const canBulkManage = canManageAnySite(userData);
  const canBulkDelete = canDeleteSiteRecord(userData);

  const manageableIds = useMemo(
    () => (sites || []).filter((site) => canEditSiteRecord(userData, site._id)).map((site) => site._id),
    [sites, userData]
  );

  const selectedSites = useMemo(
    () => (sites || []).filter((site) => selectedIds.includes(site._id)),
    [sites, selectedIds]
  );

  const allManageableSelected =
    manageableIds.length > 0 && manageableIds.every((id) => selectedIds.includes(id));
  const someManageableSelected = manageableIds.some((id) => selectedIds.includes(id));

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (manageableIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !manageableIds.includes(id));
      }
      return Array.from(new Set([...prev, ...manageableIds]));
    });
  }, [manageableIds]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const columns = useMemo(
    () => [
      ...(canBulkManage
        ? [
            {
              header: (
                <Checkbox
                  size="small"
                  checked={allManageableSelected}
                  indeterminate={someManageableSelected && !allManageableSelected}
                  onChange={toggleAll}
                  disabled={!manageableIds.length}
                  sx={bulkCheckboxSx}
                />
              ) as unknown as string,
              align: "center" as const,
              accessor: (row: SiteModel) => {
                const canSelect = canEditSiteRecord(userData, row._id);
                if (!canSelect) return null;
                return (
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(row._id)}
                    onChange={() => toggleOne(row._id)}
                    sx={bulkCheckboxSx}
                  />
                );
              }
            }
          ]
        : []),
      { header: t["ID"], accessor: "siteId" as const },
      { header: t["Name"], accessor: "name" as const, sort: "name" as const },
      { header: t["City"], accessor: "city" as const, sort: "city" as const },
      {
        header: t["Country"],
        accessor: (row: SiteModel) => getCountryName(row.country),
        sort: "country" as const
      },
      {
        header: t["Added On"],
        sort: "createdAt" as const,
        accessor: (row: SiteModel) => dateFormat(row.createdAt)
      },
      ...(showActions
        ? [
            {
              header: t["Actions"],
              align: "center" as const,
              accessor: (row: SiteModel) => <SiteRowActions site={row} onRemove={setRemoving} />
            }
          ]
        : [])
    ],
    [
      t,
      showActions,
      canBulkManage,
      allManageableSelected,
      someManageableSelected,
      manageableIds.length,
      selectedIds,
      toggleAll,
      toggleOne,
      userData
    ]
  );

  return (
    <PrivateLayout canAccess={() => true}>
      {removing && (
        <RemoveSite removing={removing} onSuccess={onReload} onClose={() => setRemoving(undefined)} />
      )}
      {bulkManage && selectedSites.length > 0 && (
        <BulkManageSites
          sites={selectedSites}
          onSuccess={() => {
            setSelectedIds([]);
            onReload();
          }}
          onClose={() => setBulkManage(false)}
        />
      )}
      {bulkRemove && selectedSites.length > 0 && (
        <BulkRemoveSites
          sites={selectedSites}
          onSuccess={() => {
            setSelectedIds([]);
            onReload();
          }}
          onClose={() => setBulkRemove(false)}
        />
      )}
      <PageToolbar
        title={t["Site Records"]}
        search={search}
        searchPlaceholder={t["Search sites by name, address or city."]}
        onSearchChange={setSearch}
        breadcrumbs={
          <Breadcrumbs aria-label="breadcrumb">
            <Tooltip title={t["Home"]}>
              <IconButton LinkComponent={Link} {...{ to: "/" }} size="small" sx={{ color: COLORS.surface.secondaryText }}>
                <Home fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography color="text.primary">{t["Sites"]}</Typography>
          </Breadcrumbs>
        }
        actions={
          <>
            {userData?.type === "super" ? (
              <Tooltip title={t["Add Site"]}>
                <IconButton
                  LinkComponent={Link}
                  {...{ to: `/sites/add` }}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            ) : null}
            {isLoading ? (
              <IconButton disabled>
                <CircularProgress size={20} sx={{ color: COLORS.primary }} />
              </IconButton>
            ) : (
              <Tooltip title={t["Refresh"]}>
                <IconButton
                  onClick={onReload}
                  sx={{
                    bgcolor: COLORS.surface.card,
                    color: COLORS.surface.primaryText,
                    border: `1px solid ${COLORS.surface.border}`,
                    borderRadius: `${COLORS.radius.sm}px`,
                    "&:hover": { bgcolor: COLORS.surface.tableHeader }
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            )}
          </>
        }
      />
      <Box width="100%">
        <StyledCard>
          <CardContent sx={{ pt: 2 }}>
            <BulkActionBar
              count={selectedIds.length}
              label={selectedIds.length === 1 ? "site selected" : "sites selected"}
              onClear={() => setSelectedIds([])}
              clearLabel={t["Clear Selection"] || "Clear Selection"}
            >
              <Button variant="contained" onClick={() => setBulkManage(true)} sx={bulkPrimaryButtonSx}>
                {t["Manage Models"] || "Manage Models"}
              </Button>
              {canBulkDelete ? (
                <Button variant="contained" onClick={() => setBulkRemove(true)} sx={bulkDeleteButtonSx}>
                  {t["Delete"]}
                </Button>
              ) : null}
            </BulkActionBar>
            <DataTable
              rows={sites || null}
              isLoading={isLoading}
              error={error}
              sort={sort}
              isAscending={isAscending}
              onSort={onSort}
              columns={columns}
              selectedIds={selectedIds}
              getRowId={(row) => row._id}
            />
          </CardContent>
          <CardActions sx={{ justifyContent: "flex-end" }}>
            <StyledTablePagination
              component="div"
              align="right"
              count={totalRecords || 0}
              page={page}
              onPageChange={onPageChange}
              rowsPerPage={limit}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          </CardActions>
        </StyledCard>
      </Box>
    </PrivateLayout>
  );
}
